"use client";

import { useState, useEffect } from 'react';
import JobFilters, { FilterState } from './JobFilters';
import JobCard, { Job } from './JobCard';
import Icon from '@/components/ui/AppIcon';
import ClinicianApplicationModal from '@/components/common/ClinicianApplicationModal';


const allJobs: Job[] = [
  {
    id: 'job_1',
    title: 'RN ICU - Night Shift',
    specialty: 'ICU',
    facilityType: 'Level 1 Trauma Center',
    location: 'Phoenix, AZ',
    payRange: '$85,000 - $105,000',
    shift: '7p-7a, 3x12',
    postedDate: '2 days ago',
    roleType: 'nursing',
  },
  {
    id: 'job_2',
    title: 'RN Emergency Department',
    specialty: 'ER',
    facilityType: 'Community Hospital',
    location: 'Austin, TX',
    payRange: '$78,000 - $95,000',
    shift: 'Rotating, 3x12',
    postedDate: '5 days ago',
    roleType: 'nursing',
  },
  {
    id: 'job_3',
    title: 'Respiratory Therapist',
    specialty: 'Respiratory',
    facilityType: 'Teaching Hospital',
    location: 'Seattle, WA',
    payRange: '$72,000 - $88,000',
    shift: 'Days, 5x8',
    postedDate: '1 week ago',
    roleType: 'allied',
  },
  {
    id: 'job_4',
    title: 'CT/MRI Technologist',
    specialty: 'Radiology',
    facilityType: 'Imaging Center',
    location: 'Denver, CO',
    payRange: '$68,000 - $82,000',
    shift: 'Days, 5x8',
    postedDate: '3 days ago',
    roleType: 'allied',
  },
  {
    id: 'job_5',
    title: 'Family Medicine Nurse Practitioner',
    specialty: 'Family Medicine',
    facilityType: 'Primary Care Clinic',
    location: 'Portland, OR',
    payRange: '$110,000 - $130,000',
    shift: 'M-F, 8-5',
    postedDate: '1 day ago',
    roleType: 'advanced',
  },
  {
    id: 'job_6',
    title: 'CRNA - Operating Room',
    specialty: 'Anesthesia',
    facilityType: 'Surgical Center',
    location: 'Nashville, TN',
    payRange: '$180,000 - $220,000',
    shift: 'Days, call rotation',
    postedDate: '4 days ago',
    roleType: 'advanced',
  },
  {
    id: 'job_7',
    title: 'Hospitalist Physician',
    specialty: 'Internal Medicine',
    facilityType: 'Community Hospital',
    location: 'Charlotte, NC',
    payRange: '$240,000 - $280,000',
    shift: '7 on / 7 off',
    postedDate: '1 week ago',
    roleType: 'provider',
  },
  {
    id: 'job_8',
    title: 'Emergency Medicine Physician',
    specialty: 'Emergency Medicine',
    facilityType: 'Level 2 Trauma Center',
    location: 'Tampa, FL',
    payRange: '$280,000 - $340,000',
    shift: 'Flexible scheduling',
    postedDate: '2 days ago',
    roleType: 'provider',
  },
  {
    id: 'job_9',
    title: 'RN Med Surg - Days',
    specialty: 'Med Surg',
    facilityType: 'Regional Medical Center',
    location: 'Raleigh, NC',
    payRange: '$70,000 - $85,000',
    shift: '7a-7p, 3x12',
    postedDate: '6 days ago',
    roleType: 'nursing',
  },
  {
    id: 'job_10',
    title: 'Surgical Technologist',
    specialty: 'Surgical',
    facilityType: 'Orthopedic Hospital',
    location: 'San Diego, CA',
    payRange: '$65,000 - $78,000',
    shift: 'Days, on-call',
    postedDate: '3 days ago',
    roleType: 'allied',
  },
  {
    id: 'job_11',
    title: 'Physician Assistant - Cardiology',
    specialty: 'Cardiology',
    facilityType: 'Specialty Practice',
    location: 'Boston, MA',
    payRange: '$115,000 - $135,000',
    shift: 'M-F, 8-5',
    postedDate: '1 week ago',
    roleType: 'advanced',
  },
  {
    id: 'job_12',
    title: 'Psychiatrist - Outpatient',
    specialty: 'Psychiatry',
    facilityType: 'Mental Health Clinic',
    location: 'Minneapolis, MN',
    payRange: '$260,000 - $300,000',
    shift: 'M-F, flexible hours',
    postedDate: '5 days ago',
    roleType: 'provider',
  },
];

export default function JobsInteractive() {
  const [filters, setFilters] = useState<FilterState>({
    roleType: 'all',
    location: '',
    specialty: '',
  });
  const [clinicianModalOpen, setClinicianModalOpen] = useState(false);
  const [selectedRoleType, setSelectedRoleType] = useState<string>('All');
  const [selectedSpecialty, setSelectedSpecialty] = useState<string>('All');
  const [selectedLocation, setSelectedLocation] = useState<string>('All');
  const [selectedShift, setSelectedShift] = useState<string>('All');

  const [filteredJobs, setFilteredJobs] = useState<Job[]>(allJobs);
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleFilterChange = (filters: FilterState) => {
    let filtered = allJobs;

    if (filters.roleType) {
      filtered = filtered.filter((job) => job.roleType === filters.roleType);
    }

    if (filters.specialty) {
      filtered = filtered.filter((job) =>
        job.specialty.toLowerCase().includes(filters.specialty.toLowerCase())
      );
    }

    if (filters.location) {
      filtered = filtered.filter((job) =>
        job.location.toLowerCase().includes(filters.location.toLowerCase())
      );
    }

    setFilteredJobs(filtered);
  };

  if (!isHydrated) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        <div className="lg:col-span-1">
          <div className="bg-white p-6 rounded-xl border border-border">
            <div className="h-8 bg-accent rounded mb-4"></div>
            <div className="space-y-4">
              <div className="h-10 bg-accent rounded"></div>
              <div className="h-10 bg-accent rounded"></div>
              <div className="h-10 bg-accent rounded"></div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={`skeleton_${i}`} className="bg-white p-6 rounded-xl border border-border">
                <div className="h-6 bg-accent rounded mb-4 w-3/4"></div>
                <div className="space-y-2">
                  <div className="h-4 bg-accent rounded w-1/2"></div>
                  <div className="h-4 bg-accent rounded w-2/3"></div>
                  <div className="h-4 bg-accent rounded w-1/2"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="max-w-7xl mx-auto px-6">
        {/* Filters Sidebar */}
        <div className="lg:col-span-1">
          <JobFilters onFilterChange={handleFilterChange} />
        </div>

        {/* Job Listings */}
        <div className="lg:col-span-3">
          {filteredJobs?.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 bg-[#f8faf9] rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="MagnifyingGlassIcon" size={32} className="text-[#3a4d46]" />
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-[#26342f] mb-4">
                No jobs match your filters
              </h3>
              <p className="text-base sm:text-lg text-[#3a4d46] mb-8">
                Try adjusting your search criteria or apply directly to get matched with opportunities.
              </p>
              <button
                onClick={() => setClinicianModalOpen(true)}
                className="inline-flex items-center px-8 py-4 text-base font-semibold text-white bg-gradient-to-r from-[#26342f] to-[#3a4d46] rounded-xl hover:shadow-lg transition-all"
              >
                Apply for jobs
                <Icon name="ArrowRightIcon" size={20} className="ml-2" />
              </button>
            </div>
          ) : (
            <>
              <div className="mb-6 flex items-center justify-between">
                <p className="text-muted-foreground">
                  Showing{' '}
                  <span className="font-semibold text-foreground">
                    {filteredJobs.length}
                  </span>{' '}
                  jobs
                </p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} />
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal */}
      <ClinicianApplicationModal isOpen={clinicianModalOpen} onClose={() => setClinicianModalOpen(false)} />
    </>
  );
}